import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Tooltip,
  Typography,
} from '@mui/material';
import AddTaskIcon from '@mui/icons-material/AddTask';
import EditNoteIcon from '@mui/icons-material/EditNote';
import TuneIcon from '@mui/icons-material/Tune';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import { useTranslation } from 'react-i18next';
import type { UnifiedRecommendation, RecommendationTarget, TaskSourceType, AuditType } from '@traaker/shared';
import { tasksApi } from '../../services/tasks';
import { proposedArticlesApi } from '../../services/proposed-articles';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { StrategySuggestDialog } from './StrategySuggestDialog';
import QuotaUpgradeDialog from '../billing/QuotaUpgradeDialog';

const IMPACT_LABEL_KEY: Record<string, string> = { high: 'recommendationCard.impact.high', medium: 'recommendationCard.impact.medium', low: 'recommendationCard.impact.low' };
const IMPACT_COLOR: Record<string, string> = { high: '#15803D', medium: '#92400E', low: '#6B7280' };
const EFFORT_LABEL_KEY: Record<string, string> = { high: 'recommendationCard.effort.high', medium: 'recommendationCard.effort.medium', low: 'recommendationCard.effort.low' };
const EFFORT_COLOR: Record<string, string> = { high: '#EF4444', medium: '#F59E0B', low: '#10B981' };

/** Target → icon + accent + i18n action-label keys. */
const TARGET_META: Record<
  RecommendationTarget,
  { Icon: typeof AddTaskIcon; color: string; bg: string; labelKey: string; ctaKey: string; doneKey: string }
> = {
  task: { Icon: AddTaskIcon, color: '#7C3AED', bg: '#7C3AED14', labelKey: 'recommendationCard.target.task.label', ctaKey: 'recommendationCard.target.task.cta', doneKey: 'recommendationCard.target.task.done' },
  content: { Icon: EditNoteIcon, color: '#0284C7', bg: '#0EA5E914', labelKey: 'recommendationCard.target.content.label', ctaKey: 'recommendationCard.target.content.cta', doneKey: 'recommendationCard.target.content.done' },
  geo_strategy: { Icon: TuneIcon, color: '#B45309', bg: '#F59E0B14', labelKey: 'recommendationCard.target.geoStrategy.label', ctaKey: 'recommendationCard.target.geoStrategy.cta', doneKey: 'recommendationCard.target.geoStrategy.done' },
};

/** Map an AuditType to the Task sourceType enum. */
function taskSourceType(auditType: AuditType): TaskSourceType {
  if (auditType === 'website') return 'audit_website';
  if (auditType === 'earned_media') return 'audit_earned_media';
  if (auditType.startsWith('brand')) return 'audit_brand';
  return 'manual';
}

/** Map an AuditType to the strategy-suggest `axis` seed. */
function suggestAxis(auditType: AuditType): string {
  if (auditType === 'brand_narrative') return 'narrative';
  if (auditType === 'brand_authority') return 'authority';
  if (auditType === 'brand_referral') return 'referral';
  return auditType;
}

export function RecommendationCard({
  reco,
  auditId,
  brandId,
  onActioned,
}: {
  reco: UnifiedRecommendation;
  auditId?: string;
  brandId?: string;
  /** Called after the reco is successfully actioned (add task / studio / apply
   *  strategy) so the parent can hide + persist-dismiss it. */
  onActioned?: () => void;
}) {
  const { t } = useTranslation();
  const { tenant } = useAuth();
  const toast = useToast();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [geoOpen, setGeoOpen] = useState(false);
  const [quotaDialogOpen, setQuotaDialogOpen] = useState(false);

  const tasksEnabled = !!tenant?.settings?.featureFlags?.tasksEnabled;
  const newsWatchEnabled = !!tenant?.settings?.featureFlags?.newsWatchEnabled;

  // A content-target reco needs News Watch (Studio); a task-target reco needs
  // Tasks. If the required feature is off, fall back to the task path so the
  // recommendation is never a dead end (unless tasks are also off).
  let target = reco.target;
  if (target === 'content' && !newsWatchEnabled) target = 'task';
  if (target === 'task' && !tasksEnabled) {
    // Tasks off: keep the button hidden entirely below.
  }
  const meta = TARGET_META[target];

  const addTask = async () => {
    await tasksApi.create({
      title: reco.title.slice(0, 120),
      description: reco.description || reco.title,
      category: reco.category,
      sourceType: taskSourceType(reco.auditType),
      auditId,
      brandId,
      sourcePageUrl: reco.sourcePageUrl,
      sourceDimension: reco.sourceDimension,
      sourceAuditType: reco.auditType,
      originLabel: reco.originLabel,
    });
    toast.show(t('recommendationCard.toast.addedToTasks'), { action: { label: t('recommendationCard.toast.view'), onClick: () => navigate('/tasks') } });
  };

  // Content target does NOT generate — it SAVES the reco as a reusable "content
  // topic". The topic then appears in the Studio's section 1 ("À partir d'une
  // recommandation") for the user to pick and generate when ready.
  const addContentTopic = async () => {
    if (!brandId) throw new Error('brandId required');
    await proposedArticlesApi.createContentTopic({
      brandId,
      topic: `${reco.title}${reco.description ? ` — ${reco.description}` : ''}`.trim(),
      originLabel: `${t(meta.labelKey)} · ${reco.originLabel}`,
      sourceAuditId: auditId,
    });
    toast.show(t('recommendationCard.toast.addedToStudio'), {
      action: { label: t('recommendationCard.toast.view'), onClick: () => navigate('/news-watch', { state: { focusReco: true } }) },
    });
  };

  const handleClick = async () => {
    if (busy || done) return;
    if (target === 'geo_strategy') {
      setGeoOpen(true);
      return;
    }
    setBusy(true);
    // Optimistic: flip to "done" immediately so the UI always confirms — the
    // write is navigate-away-safe.
    try {
      if (target === 'content') await addContentTopic();
      else await addTask();
      setDone(true);
      // Briefly show "✓ Ajouté", then let the parent hide + persist-dismiss it.
      setTimeout(() => onActioned?.(), 1200);
    } catch (err: any) {
      if (err?.body?.code === 'QUOTA_EXCEEDED') {
        setQuotaDialogOpen(true);
      } else {
        toast.show(t('recommendationCard.toast.actionFailed'), { severity: 'error' });
      }
    } finally {
      setBusy(false);
    }
  };

  // Hide the action when the only viable target (task) is disabled.
  const actionHidden = target === 'task' && !tasksEnabled;
  const { Icon } = meta;

  return (
    <Card variant="outlined" sx={{ height: '100%', display: 'flex', flexDirection: 'column', borderColor: 'rgba(11,11,15,0.08)' }}>
      <CardContent sx={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 1, p: 2 }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1.25 }}>
          <Box
            sx={{
              width: 34,
              height: 34,
              flexShrink: 0,
              borderRadius: 1.5,
              bgcolor: meta.bg,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Tooltip title={t(meta.labelKey)}>
              <Icon sx={{ fontSize: 20, color: meta.color }} />
            </Tooltip>
          </Box>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="subtitle2" fontWeight={700} sx={{ lineHeight: 1.35 }}>
              {reco.title}
            </Typography>
            {reco.description && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                {reco.description}
              </Typography>
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, mt: 'auto', pt: 1.25, alignItems: 'center', flexWrap: 'wrap' }}>
          {reco.impact && (
            <Chip
              size="small"
              label={t(IMPACT_LABEL_KEY[reco.impact])}
              sx={{ height: 22, bgcolor: `${IMPACT_COLOR[reco.impact]}18`, color: IMPACT_COLOR[reco.impact], fontWeight: 700, fontSize: 11 }}
            />
          )}
          {reco.effort && (
            <Chip
              size="small"
              label={t(EFFORT_LABEL_KEY[reco.effort])}
              sx={{ height: 22, bgcolor: `${EFFORT_COLOR[reco.effort]}18`, color: EFFORT_COLOR[reco.effort], fontWeight: 700, fontSize: 11 }}
            />
          )}
          <Box sx={{ flex: 1 }} />
          {!actionHidden && (
            <Button
              size="small"
              variant={done ? 'text' : 'contained'}
              disableElevation
              startIcon={
                busy ? <CircularProgress size={14} color="inherit" /> : done ? <CheckCircleOutlineIcon fontSize="small" /> : <Icon fontSize="small" />
              }
              disabled={busy || done}
              onClick={handleClick}
              sx={{
                textTransform: 'none',
                fontWeight: 600,
                ...(done
                  ? { color: '#00A88A' }
                  : { bgcolor: meta.color, '&:hover': { bgcolor: meta.color, filter: 'brightness(0.92)' } }),
              }}
            >
              {done ? t(meta.doneKey) : t(meta.ctaKey)}
            </Button>
          )}
        </Box>
      </CardContent>

      {target === 'geo_strategy' && brandId && (
        <StrategySuggestDialog
          open={geoOpen}
          onClose={() => setGeoOpen(false)}
          brandId={brandId}
          auditId={auditId}
          seedQuery={reco.title}
          seedResponse={reco.description || reco.title}
          axis={suggestAxis(reco.auditType)}
          onApplied={() => {
            setDone(true);
            toast.show(t('recommendationCard.toast.strategyUpdated'));
            setTimeout(() => onActioned?.(), 1200);
          }}
        />
      )}
      <QuotaUpgradeDialog
        open={quotaDialogOpen}
        onClose={() => setQuotaDialogOpen(false)}
        highlightMetric="tasks"
      />
    </Card>
  );
}
